/**
 * Shared Contact API functions
 * Contacts are used by multiple features (Customer, Supplier, etc.)
 */

import { apiClient, type PaginatedResponse } from '../../../api/client'
import buildApiUrl, { buildPaginationParams, type PaginationOptions } from '../../../api/pagination'
import {
  type PaginatedQueryParameters,
  type GenericFilterType,
} from '../../../api/hooks/utils/types'
import { type EntitySearchResponse } from '../../../hooks/useEntitySearch'
import LogTool from '../../../logger/logTools'
import { createAddress, getPKfromSelf, parseNestedObjectList } from '../../../utils/functions'
import { type Contact, type RichContact, type Address, ContactGroup } from '../../../utils/types'
import { APIContact, APIContactFromJSON } from '../../../generated-types/models/APIContact'
import {
  APIRichContact,
  APIRichContactFromJSON,
} from '../../../generated-types/models/APIRichContact'
import { APIContactGroup, APIContactGroupFromJSON } from '../../../generated-types/models/APIContactGroup'

const log = new LogTool({ context: 'ContactAPI', enable: true, logLevel: 'warn' })

/**
 * Filter types for Contact queries
 */
export interface ContactFilters extends GenericFilterType {
  first_name?: string
  last_name?: string
  email?: string
  phone?: string
  language?: string
  organization?: string
  created_at?: string
}

/**
 * Filter types for ContactGroup queries
 */
export interface ContactGroupFilters extends GenericFilterType {
  name?: string
  contact?: string
}

/**
 * Create Contact from API response
 */
export function createContact(contactResponse: any): Contact {
  const apiContact: APIContact = APIContactFromJSON(contactResponse)
  return {
    ...apiContact,
    key: getPKfromSelf(apiContact.self),
    addresses: parseNestedObjectList(contactResponse.addresses, createAddress),
  }
}

/**
 * Create RichContact from API response
 */
export function createRichContact(contactResponse: any): RichContact {
  const apiContact: APIRichContact = APIRichContactFromJSON(contactResponse)
  return {
    ...apiContact,
    key: getPKfromSelf(apiContact.self),
    addresses: parseNestedObjectList(contactResponse.addresses, createAddress),
    suppliers: contactResponse.suppliers ? contactResponse.suppliers : [],
    customers: contactResponse.customers ? contactResponse.customers : [],
  }
}

export function createContactGroup(contactGroupResponse: any): ContactGroup {
  const apiContactGroup: APIContactGroup = APIContactGroupFromJSON(contactGroupResponse)
  return {
    ...apiContactGroup,
    key: getPKfromSelf(apiContactGroup.self),
    contacts: parseNestedObjectList(contactGroupResponse.contacts, createContact),
  }
}

/**
 * Convert form input to API JSON
 */
export function formInputToAPIContactJSON(input: any): any {
  return {
    ...(input.email && { email: input.email }),
    ...(input.phone && { phone: input.phone }),
    ...(input.firstName && { first_name: input.firstName }),
    ...(input.lastName && { last_name: input.lastName }),
    ...(input.language && { language: input.language }),
    ...(input.comment && { comment: input.comment }),
    ...(input.addresses && { addresses: input.addresses }),
  }
}

/**
 * Fetch paginated contacts from API
 */
export async function fetchContacts(
  options: PaginatedQueryParameters<Contact, ContactFilters> | PaginationOptions = {},
  ids?: number[]
): Promise<PaginatedResponse<Contact>> {
  log.info('Fetching contacts with options:', options)

  const url = buildApiUrl(
    'v1/contacts/contacts/',
    options as PaginatedQueryParameters<Contact, ContactFilters>,
    ids
  )
  const response = await apiClient.get<PaginatedResponse<any>>(url)

  const contacts = (response.results || []).map((c: any) => createContact(c))

  return {
    ...response,
    results: contacts,
  }
}

/**
 * Search contacts using Elasticsearch
 */
export async function searchContacts(
  options: PaginatedQueryParameters<Contact, ContactFilters> = {}
): Promise<EntitySearchResponse<Contact>> {
  // Ensure q parameter is set - use '*' (match all) if filters are present but no q is set
  const filters = options.filters || {}
  const hasFilters = Object.keys(filters).length > 0
  const hasQ = filters.q !== undefined && filters.q !== null && filters.q !== ''

  // Convert filter keys to match backend API expectations
  // Backend expects 'name' parameter, not 'first_name', 'last_name', 'firstName', or 'lastName'
  const searchFilters: any = { ...filters }

  // Convert firstName/lastName to name parameter if present
  const firstName = filters.first_name || (filters as any).firstName
  const lastName = filters.last_name || (filters as any).lastName

  if (firstName || lastName) {
    const nameParts = [firstName, lastName].filter(Boolean).join(' ').trim()
    if (nameParts) {
      searchFilters.name = nameParts
      // Remove individual name filters to avoid conflicts
      delete searchFilters.first_name
      delete searchFilters.last_name
      delete searchFilters.firstName
      delete searchFilters.lastName
    }
  }

  // If filters are present but no q parameter, use '*' to match all and filter by other parameters
  if (!hasQ && hasFilters) {
    searchFilters.q = '*'
  }

  const url = buildApiUrl('v1/search/contacts/', { filters: searchFilters })
  return apiClient.get<EntitySearchResponse<Contact>>(url)
}

/**
 * Fetch paginated rich contacts from API
 */
export async function fetchRichContacts(
  options: PaginationOptions = {}
): Promise<PaginatedResponse<RichContact>> {
  log.info('Fetching rich contacts with options:', options)

  const params = buildPaginationParams(options)
  const response = await apiClient.get<PaginatedResponse<any>>(
    `v1/contacts/rich_contacts/?${params.join('&')}`
  )

  const contacts = response.results.map((c: any) => createRichContact(c))

  return {
    ...response,
    results: contacts,
  }
}

/**
 * Fetch a single contact by ID or URL
 */
export async function fetchContact(idOrUrl: string | number, expand?: string[]): Promise<Contact> {
  log.info('Fetching contact:', idOrUrl)

  const url =
    typeof idOrUrl === 'number' || !idOrUrl.includes('/')
      ? `v1/contacts/contacts/${idOrUrl}/`
      : idOrUrl

  const params = expand ? [`expand=${expand.join(',')}`] : []
  const fetchUrl = params.length > 0 ? `${url}?${params.join('&')}` : url

  const response = await apiClient.get<any>(fetchUrl)
  return createContact(response)
}

/**
 * Create a new contact
 */
export async function createContactApi(contactData: any): Promise<Contact> {
  log.info('Creating contact:', contactData)

  const json = formInputToAPIContactJSON(contactData)
  // Don't use ?expand=addresses in POST - it activates nested serializer which expects address objects, not URLs
  const response = await apiClient.post<any>('v1/contacts/contacts/', json)

  return createContact(response)
}

/**
 * Update an existing contact
 */
export async function updateContactApi(
  idOrSelf: string | number,
  contactData: any
): Promise<Contact> {
  log.info('Updating contact:', idOrSelf, contactData)

  // Don't use ?expand=addresses in PUT - it activates nested serializer which expects address objects, not URLs
  const url =
    typeof idOrSelf === 'number' || !idOrSelf.includes('/')
      ? `v1/contacts/contacts/${idOrSelf}/`
      : idOrSelf

  const json = formInputToAPIContactJSON(contactData)
  const response = await apiClient.put<any>(url, json)

  return createContact(response)
}

/**
 * Patch an existing contact (partial update)
 */
export async function patchContactApi(
  idOrSelf: string | number,
  contactData: Partial<any>
): Promise<Contact> {
  log.info('Patching contact:', idOrSelf, contactData)

  // Don't use ?expand=addresses in PATCH - it activates nested serializer which expects address objects, not URLs
  const url =
    typeof idOrSelf === 'number' || !idOrSelf.includes('/')
      ? `v1/contacts/contacts/${idOrSelf}/`
      : idOrSelf

  const json = formInputToAPIContactJSON(contactData)
  const response = await apiClient.patch<any>(url, json)

  return createContact(response)
}

/**
 * Delete a contact
 */
export async function deleteContactApi(idOrSelf: string | number): Promise<void> {
  log.info('Deleting contact:', idOrSelf)

  const url =
    typeof idOrSelf === 'number' || !idOrSelf.includes('/')
      ? `v1/contacts/contacts/${idOrSelf}/`
      : idOrSelf

  await apiClient.delete(url)
}

/**
 * Delete multiple contacts
 */
export async function deleteContactsApi(contacts: Contact[]): Promise<void> {
  log.info('Deleting contacts:', contacts.length)

  const deletePromises = contacts.map(contact => deleteContactApi(contact.self))
  await Promise.all(deletePromises)
}

/**
 * Fetch all contacts (recursively fetches all pages)
 * Note: Use with caution for large datasets
 */
export async function fetchAllContacts(
  options: Omit<PaginationOptions, 'page'> = {}
): Promise<Contact[]> {
  log.info('Fetching all contacts')

  let allContacts: Contact[] = []
  let page = 1
  let hasMore = true

  while (hasMore) {
    const response = await fetchContacts({ ...options, page })
    allContacts = [...allContacts, ...response.results]

    hasMore = response.next !== null
    page++
  }

  log.info(`Fetched ${allContacts.length} contacts total`)
  return allContacts
}

/**
 * Fetch all rich contacts (recursively fetches all pages)
 * Note: Use with caution for large datasets
 */
export async function fetchAllRichContacts(
  options: Omit<PaginationOptions, 'page'> = {}
): Promise<RichContact[]> {
  log.info('Fetching all rich contacts')

  let allContacts: RichContact[] = []
  let page = 1
  let hasMore = true

  while (hasMore) {
    const response = await fetchRichContacts({ ...options, page })
    allContacts = [...allContacts, ...response.results]

    hasMore = response.next !== null
    page++
  }

  log.info(`Fetched ${allContacts.length} rich contacts total`)
  return allContacts
}

/**
 * Fetch contacts as options for select inputs (first page)
 * Returns options in the format: { value: string, label: string }
 * Label format: "firstName lastName"
 */
export async function fetchContactOptions(): Promise<
  { value: string; label?: string; disabled?: boolean }[]
> {
  log.info('Fetching contact options')

  const response = await fetchContacts({ page: 1 })

  return response.results
    .filter(contact => contact.self !== undefined)
    .map(contact => ({
      value: contact.self,
      label: `${contact.firstName ?? ''} ${contact.lastName ?? ''}`.trim(),
    }))
}

/**
 * Fetch all contacts as options for select inputs (all pages)
 * Returns options in the format: { value: string, label: string }
 * Label format: "firstName lastName" or email if no name available
 * Note: Use with caution for large datasets
 */
export async function fetchAllContactOptions(): Promise<
  { value: string; label?: string; disabled?: boolean }[]
> {
  log.info('Fetching all contact options')

  const allContacts = await fetchAllContacts()

  return allContacts
    .filter(contact => contact.self !== undefined)
    .map(contact => {
      const hasName = contact.firstName || contact.lastName
      const label = hasName
        ? `${contact.firstName ?? ''} ${contact.lastName ?? ''}`.trim()
        : (contact.email ?? '')

      return {
        value: contact.self,
        label,
      }
    })
}

/**
 * Convert form input to API JSON
 */
export function formInputToAPIContactGroupJSON(input: any): any {
  return {
    ...(input.name && { name: input.name }),
    ...(input.contacts && { contacts: input.contacts }),
  }
}

/**
 * Fetch paginated contact groups from API
 */
export async function fetchContactGroups(
  options: PaginatedQueryParameters<ContactGroup, ContactGroupFilters> | PaginationOptions = {},
  ids?: number[]
): Promise<PaginatedResponse<ContactGroup>> {
  log.info('Fetching contact groups with options:', options)

  const url = buildApiUrl(
    'v1/contacts/contact_groups/',
    options as PaginatedQueryParameters<ContactGroup, ContactGroupFilters>,
    ids
  )
  const response = await apiClient.get<PaginatedResponse<any>>(url)

  const contactGroups = (response.results || []).map(createContactGroup)

  return {
    ...response,
    results: contactGroups,
  }
}

/**
 * Fetch all contact gropus (recursively fetches all pages)
 * Note: Use with caution for large datasets
 */
export async function fetchAllContactGroups(
  options: Omit<PaginationOptions, 'page'> = {}
): Promise<ContactGroup[]> {
  log.info('Fetching all contact groups')

  let allContactGroups: ContactGroup[] = []
  let page = 1
  let hasMore = true

  while (hasMore) {
    const response = await fetchContactGroups({ ...options, page })
    allContactGroups = [...allContactGroups, ...response.results]

    hasMore = response.next !== null
    page++
  }

  log.info(`Fetched ${allContactGroups.length} contact groups total`)
  return allContactGroups
}

/**
 * Search contact groups using Elasticsearch
 */
export async function searchContactGroups(
  options: PaginatedQueryParameters<ContactGroup, ContactGroupFilters> = {}
): Promise<EntitySearchResponse<ContactGroup>> {
  // Ensure q parameter is set - use '*' (match all) if filters are present but no q is set
  const filters = options.filters || {}
  const hasFilters = Object.keys(filters).length > 0
  const hasQ = filters.q !== undefined && filters.q !== null && filters.q !== ''

  const searchFilters: any = { ...filters }

  // If filters are present but no q parameter, use '*' to match all and filter by other parameters
  if (!hasQ && hasFilters) {
    searchFilters.q = '*'
  }

  const url = buildApiUrl('v1/search/contacts/', { filters: searchFilters })
  return apiClient.get<EntitySearchResponse<ContactGroup>>(url)
}

/**
 * Fetch a single contact group by ID or URL
 */
export async function fetchContactGroup(
  idOrUrl: string | number,
  expand?: string[]
): Promise<ContactGroup> {
  log.info('Fetching contact group:', idOrUrl)

  const url =
    typeof idOrUrl === 'number' || !idOrUrl.includes('/')
      ? `v1/contacts/contact_groups/${idOrUrl}/`
      : idOrUrl

  const params = expand ? [`expand=${expand.join(',')}`] : []
  const fetchUrl = params.length > 0 ? `${url}?${params.join('&')}` : url

  const response = await apiClient.get<any>(fetchUrl)
  return createContactGroup(response)
}

/**
 * Create a new contact
 */
export async function createContactGroupApi(contactGroupData: any): Promise<ContactGroup> {
  log.info('Creating contact group:', contactGroupData)

  const json = formInputToAPIContactGroupJSON(contactGroupData)
  // Don't use ?expand=addresses in POST - it activates nested serializer which expects address objects, not URLs
  const response = await apiClient.post<any>('v1/contacts/contact_groups/', json)

  return createContactGroup(response)
}

/**
 * Update an existing contact
 */
export async function updateContactGroupApi(
  idOrSelf: string | number,
  contactGroupData: any
): Promise<ContactGroup> {
  log.info('Updating contact group:', idOrSelf, contactGroupData)

  // Don't use ?expand=addresses in PUT - it activates nested serializer which expects address objects, not URLs
  const url =
    typeof idOrSelf === 'number' || !idOrSelf.includes('/')
      ? `v1/contacts/contact_groups/${idOrSelf}/`
      : idOrSelf

  const json = formInputToAPIContactGroupJSON(contactGroupData)
  const response = await apiClient.put<any>(url, json)

  return createContactGroup(response)
}

/**
 * Patch an existing contact (partial update)
 */
export async function patchContactGroupApi(
  idOrSelf: string | number,
  contactGroupData: Partial<any>
): Promise<ContactGroup> {
  log.info('Patching contact group:', idOrSelf, contactGroupData)

  // Don't use ?expand=addresses in PATCH - it activates nested serializer which expects address objects, not URLs
  const url =
    typeof idOrSelf === 'number' || !idOrSelf.includes('/')
      ? `v1/contacts/contact_groups/${idOrSelf}/`
      : idOrSelf

  const json = formInputToAPIContactGroupJSON(contactGroupData)
  const response = await apiClient.patch<any>(url, json)

  return createContactGroup(response)
}

/**
 * Delete a contact
 */
export async function deleteContactGroupApi(idOrSelf: string | number): Promise<void> {
  log.info('Deleting contact group:', idOrSelf)

  const url =
    typeof idOrSelf === 'number' || !idOrSelf.includes('/')
      ? `v1/contacts/contact_groups/${idOrSelf}/`
      : idOrSelf

  await apiClient.delete(url)
}

/**
 * Delete multiple contacts
 */
export async function deleteContactGroupsApi(contacts: ContactGroup[]): Promise<void> {
  log.info('Deleting contact groups:', contacts.length)

  const deletePromises = contacts.map(contact => deleteContactApi(contact.self))
  await Promise.all(deletePromises)
}
